import { supabase } from '../lib/supabase';
import serviceManagementService from './serviceManagementService';

/**
 * @typedef {Object} CaseServiceRow
 * @property {string} id
 * @property {string} caseId
 * @property {string} serviceId
 * @property {string} serviceName
 * @property {string} serviceCode
 * @property {string} category
 * @property {number} quantity
 * @property {number} unitPrice
 * @property {string|null} currencyCode
 * @property {number} lineTotal
 */

/**
 * @param {Array} availabilityRows - sortie getServiceAvailabilityByCountry
 * @returns {Map<string, { unitPrice: number, currencyCode: string|null, serviceName: string, serviceCode: string, category: string }>}
 */
function buildAvailabilityMap(availabilityRows, currencyCode) {
  const map = new Map();
  for (const row of availabilityRows || []) {
    if (!row?.isAvailable || row?.service?.is_active === false) continue;
    const serviceId = row.serviceId;
    if (!serviceId) continue;
    const unitPrice =
      row.customPrice != null && row.customPrice !== ''
        ? Number(row.customPrice)
        : Number(row.basePrice) || 0;
    map.set(serviceId, {
      unitPrice,
      currencyCode: currencyCode || null,
      serviceName: row.serviceName || '',
      serviceCode: row.serviceCode || '',
      category: row.category || '',
    });
  }
  return map;
}

export const caseServicesService = {
  /**
   * @param {string} caseId
   * @returns {Promise<CaseServiceRow[]>}
   */
  listByCaseId: async (caseId) => {
    const { data, error } = await supabase
      .from('case_services')
      .select(
        `
        id,
        case_id,
        service_id,
        quantity,
        unit_price,
        currency_code,
        service:services (
          service_name,
          service_code,
          category
        )
      `,
      )
      .eq('case_id', caseId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return (data || []).map((row) => {
      const qty = Number(row.quantity) || 1;
      const unitPrice = Number(row.unit_price) || 0;
      return {
        id: row.id,
        caseId: row.case_id,
        serviceId: row.service_id,
        serviceName: row.service?.service_name || '',
        serviceCode: row.service?.service_code || '',
        category: row.service?.category || '',
        quantity: qty,
        unitPrice,
        currencyCode: row.currency_code || null,
        lineTotal: qty * unitPrice,
      };
    });
  },

  /**
   * @param {string} caseId
   * @returns {Promise<string[]>}
   */
  getSelectedServiceIds: async (caseId) => {
    const rows = await caseServicesService.listByCaseId(caseId);
    return rows.map((r) => r.serviceId);
  },

  /**
   * @param {string} caseId
   * @param {string} countryId
   * @param {string[]} serviceIds
   * @param {string} [currencyCode]
   */
  replaceForCase: async (caseId, countryId, serviceIds, currencyCode = null) => {
    const uniqueIds = [...new Set((serviceIds || []).filter(Boolean))];
    const availability = await serviceManagementService.getServiceAvailabilityByCountry(countryId);
    const priceMap = buildAvailabilityMap(availability, currencyCode);

    const { error: delError } = await supabase.from('case_services').delete().eq('case_id', caseId);
    if (delError) throw delError;

    if (uniqueIds.length === 0) return [];

    const inserts = [];
    for (const serviceId of uniqueIds) {
      const meta = priceMap.get(serviceId);
      if (!meta) {
        throw new Error('Un ou plusieurs services ne sont pas disponibles pour ce pays.');
      }
      inserts.push({
        case_id: caseId,
        service_id: serviceId,
        quantity: 1,
        unit_price: meta.unitPrice,
        currency_code: meta.currencyCode,
      });
    }

    const { data, error } = await supabase.from('case_services').insert(inserts).select('id');
    if (error) throw error;
    return data || [];
  },

  /**
   * @param {string} caseId
   * @returns {Promise<{ total: number, rows: CaseServiceRow[] }>}
   */
  getTotalForCase: async (caseId) => {
    const rows = await caseServicesService.listByCaseId(caseId);
    const total = rows.reduce((sum, r) => sum + r.lineTotal, 0);
    return { total, rows };
  },

  /**
   * Services disponibles pour le pays (UI étape 4).
   * @param {string} countryId
   * @param {string} [currencyCode]
   */
  getAvailableServicesForCountry: async (countryId, currencyCode = null) => {
    const availability = await serviceManagementService.getServiceAvailabilityByCountry(countryId);
    return (availability || [])
      .filter((row) => row.isAvailable)
      .map((row) => {
        const unitPrice =
          row.customPrice != null && row.customPrice !== ''
            ? Number(row.customPrice)
            : Number(row.basePrice) || 0;
        return {
          id: row.serviceId,
          serviceName: row.serviceName,
          serviceCode: row.serviceCode,
          category: row.category,
          unitPrice,
          currencyCode,
        };
      });
  },
};

export default caseServicesService;
